// What the comparison's TOTAL leaves out, so the table ties back to the header.
//
// The comparison totals only the charges it can compare. Unmapped and
// passthrough lines have no expected side, environmental is hidden for sites
// without GreenPower, charges can be switched off by hand, and GST can be off.
// NMI 41037726865 (Aug 2026) showed TOTAL $1,017.86 vs $1,017.85 "Matched"
// while the header said $1,119.65 vs $1,973.73: $955.88 of the invoice was
// simply not compared. This lists those parts so the gap is visible and adds up.

export type NotComparedLine = {
  key: 'unmapped' | 'environmental' | 'switched_off' | 'gst';
  label: string;
  expected: number;
  retailer: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export function notCompared(input: {
  totals: { expectedTotal: number; retailerTotal: number | null; excluded: { count: number; expected: number; retailer: number } };
  /** Retailer amount of unmapped / passthrough rows in the compared sections. */
  unmappedRetailer: number;
  unmappedCount: number;
  /** Environmental section when it is hidden (site has no GreenPower); null when shown. */
  environmental: { expected: number; retailer: number } | null;
  /** GST as the calculation and the invoice state it, and whether the table has it switched off. */
  gst: { off: boolean; expected: number; retailer: number | null };
  /** The whole-bill figures the header shows. */
  header: { expected: number; invoice: number | null };
}) {
  const lines: NotComparedLine[] = [];
  if (input.unmappedRetailer !== 0) {
    lines.push({
      key: 'unmapped',
      // Charge-map pass-throughs only: unmapped charges count since 2026-10-07.
      label: `${input.unmappedCount} pass-through charge${input.unmappedCount === 1 ? '' : 's'} (charge map), added back as billed`,
      expected: 0,
      retailer: round2(input.unmappedRetailer),
    });
  }
  if (input.environmental && (input.environmental.expected !== 0 || input.environmental.retailer !== 0)) {
    lines.push({
      key: 'environmental', label: 'Environmental charges (hidden: site has no GreenPower)',
      expected: round2(input.environmental.expected), retailer: round2(input.environmental.retailer),
    });
  }
  const off = input.totals.excluded;
  if (off.count > 0) {
    lines.push({
      key: 'switched_off', label: `${off.count} charge${off.count === 1 ? '' : 's'} switched off`,
      expected: round2(off.expected), retailer: round2(off.retailer),
    });
  }
  if (input.gst.off) {
    lines.push({ key: 'gst', label: 'GST (switched off)', expected: round2(input.gst.expected), retailer: round2(input.gst.retailer ?? 0) });
  }

  const notComparedExpected = round2(lines.reduce((s, l) => s + l.expected, 0));
  const notComparedRetailer = round2(lines.reduce((s, l) => s + l.retailer, 0));
  const wholeExpected = round2(input.totals.expectedTotal + notComparedExpected);
  const wholeRetailer = round2((input.totals.retailerTotal ?? 0) + notComparedRetailer);
  const within = (a: number, b: number | null) => b == null || Math.abs(a - b) <= 0.05;

  return {
    lines,
    notComparedExpected,
    notComparedRetailer,
    wholeExpected,
    wholeRetailer,
    /** Whether compared + not compared equals the header, to the cent (5c tolerance). */
    reconciles: within(wholeExpected, input.header.expected) && within(wholeRetailer, input.header.invoice),
    /** Enough left out that a plain "Matched" on the compared charges would mislead. */
    material: Math.abs(notComparedRetailer - notComparedExpected) > 1 || notComparedRetailer > 1,
  };
}
